import { FloatingChat } from "@/components/floating-chat";
import { Sidebar } from "@/components/sidebar.tsx";
import { WhatsNewModal } from "@/components/changelog/whats-new-modal";
import { WhatsNewPill } from "@/components/changelog/whats-new-pill";
import { useCurrentUser, User, UserRole } from "@/lib/api/user.ts";
import { APP_ID } from "@/lib/constants.ts";
import { useTimezoneDetection } from "@/lib/hooks/user/use-timezone-detection";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useMentorOnboardingGate } from "@/lib/hooks/mentor-profile/use-mentor-onboarding-gate";
import { hasNonMentorRole } from "@/lib/utils/system-roles";
import { isImpersonatedSession } from "@/lib/impersonation";
import { ImpersonationBanner } from "@/components/auth/impersonation-banner";
import { SbCallsProvider } from "@/lib/sendbird-calls";
import { SendBirdProvider } from "@sendbird/uikit-react";
import {
  NotificationMuteCallBridge,
  NotificationMuteSendbirdBridge,
} from "@/components/conversations/notification-mute-bridges";
import {
  gateByLastActivity,
  LAST_ACTIVITY_STORAGE_KEY,
} from "@/lib/inactivity";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { InactivityMonitor } from "@/components/auth/inactivity-monitor";
import { useFirstMentorMessage } from "@/lib/hooks/conversations/use-first-mentor-message";
import { useEffect } from "react";
import { ChatChannelsProvider } from "@/lib/hooks/conversations/use-channels-list";

export const Route = createFileRoute("/_authenticated")({
  component: () => <AuthenticatedLayout />,
  beforeLoad: async ({ context }) => {
    const isLoginRoute = location.pathname === "/login";

    if (!context.stytchSession?.session_id || !context.user?.id) {
      console.log("Missing session or user, redirecting to login");
      throw redirect({ to: "/login" });
    }

    // Skip authentication factor check in development mode.
    // or if impersonated session
    if (
      import.meta.env.VITE_REQUIRE_MFA === "true" &&
      !isImpersonatedSession(context.stytchSession)
    ) {
      if (context.stytchSession?.authentication_factors.length === 1) {
        await context.revokeSession();
        if (!isLoginRoute) throw redirect({ to: "/login" });
      }
    }

    if (context.user.role === UserRole.MENTEE) {
      console.log("User is mentee, revoking session and redirecting");
      await context.revokeSession();
      throw redirect({ to: "/login" });
    }

    if (import.meta.env.VITE_STYTCH_PUBLIC_ENV === "production") {
      const activityGate = gateByLastActivity();
      if (activityGate === "stale") {
        await context.revokeSession();
        localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
        throw redirect({ to: "/login" });
      }
    }

    console.log("Authentication check passed, allowing access");
  },
});

function AuthenticatedLayout() {
  const { data: somethingsUser } = useCurrentUser();
  const setMentorView = useMentorView((state) => state.setMentorView);
  const { isGating } = useMentorOnboardingGate();

  const systemRoles = somethingsUser?.systemRoles ?? [];
  const mentorView = useMentorView((state) => state.mentorView);
  const pinAdminView = hasNonMentorRole(systemRoles);
  const hasMentorRole = systemRoles.includes("mentor");

  useEffect(() => {
    if (pinAdminView) {
      setMentorView(false);
    } else if (hasMentorRole) {
      setMentorView(true);
    }
  }, [pinAdminView, hasMentorRole, mentorView, setMentorView]);

  if (!somethingsUser) return null;

  // Don't mount dashboard chrome (Sendbird, timezone sync, etc.) while bouncing
  // an incomplete mentor into matching-profile onboarding.
  if (isGating) return null;

  return <AuthenticatedDashboard user={somethingsUser} />;
}

function AuthenticatedDashboard({ user }: { user: User }) {
  useTimezoneDetection();
  useFirstMentorMessage();

  return (
    <SbCallsProvider appId={APP_ID}>
      <SendBirdProvider
        appId={APP_ID}
        userId={user.id}
        accessToken={user.sendbird_access_token}
        // uikit enables the SDK's IndexedDB cache by default. Its write path
        // re-serializes whole blocks on every message/channel upsert, which was
        // ~98% of allocation churn (20-30 MB/s) in busy group chats.
        sdkInitParams={{ localCacheEnabled: false }}
        uikitOptions={{
          groupChannel: {
            enableVoiceMessage: true,
            enableReactions: true,
            replyType: "quote_reply",
          },
        }}
      >
        <ChatChannelsProvider>
          <div className="flex">
            <NotificationMuteCallBridge />
            <NotificationMuteSendbirdBridge />
            <Sidebar />
            <main className="w-screen-sidebar flex-1">
              <ImpersonationBanner />
              <Outlet />
              <FloatingChat />
              <WhatsNewPill />
              <WhatsNewModal />
              {import.meta.env.VITE_STYTCH_PUBLIC_ENV === "production" && (
                <InactivityMonitor />
              )}
            </main>
          </div>
        </ChatChannelsProvider>
      </SendBirdProvider>
    </SbCallsProvider>
  );
}
